import React, { useState, useEffect } from 'react';
import { Stack, Paper, Grid, TextField, Button, Typography, Checkbox, FormControlLabel, FormControl, InputLabel, MenuItem, Select, useTheme, useMediaQuery } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';

//components imports
import AlertInfo from '../../../dataObjects/AlertInfo';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import { GenericFormObject } from '../dataObjects/GenericFormObject';

import AuditorList from '../../../data/AuditorList'
import AuditorsDropdown from '../../../components/reference/AuditorsDropdown';

//const data
const AUDITORS = AuditorList;

interface District {
    code: number;
    description: string;
}

interface FormDataProps {
    formData: GenericFormObject;
    formType: string;
    accounTypeCode: string;
    closeGenericForm: () => void;
}

const OrganizationForm = (props: FormDataProps): JSX.Element => {

    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();

    // some helpers
    const getOcpfCommitteeName = () => {
        if (form.candidate)
            return form.candidate.lastName + " Committee";
        if (form.committeeName)
            return form.committeeName;
        if (form.committee)
            return form.committee.name;

        return "";
    }

    const getChairInfo = (data: GenericFormObject) => data.chair || data.chairperson || data.chairman;

    /*Initial State */
    const [form, setForm] = useState<GenericFormObject>(props.formData);
    const [accountTypeCode, setAccountTypeCode] = useState<string>(props.accounTypeCode);
    const [isReopen, setIsReopen] = useState<boolean>(props.formData.isReopen);
    const [committeeName, setCommitteeName] = useState<string>(getOcpfCommitteeName());
    const [isCandidateOnly, setIsCandidateOnly] = useState<boolean>(false);
    const [partyAffiliation, setPartyAffiliation] = useState<string>(props.formData.candidate?.partyAffiliation || props.formData.party || '');

    const [officeType, setOfficeType] = useState<string>("");
    const [districts, setDistricts] = useState<District[]>([]);
    const [districtId, setDistrictId] = useState<number>(-1);

    const [auditorId, setAuditorId] = useState(props.accounTypeCode === 'L' ? 410838: -1);

    const [candidateSalutation, setCandidateSalutation] = useState<string>('');
    const [treasurerSalutation, setTreasurerSalutation] = useState<string>('');
    const [chairSalutation, setChairSalutation] = useState<string>('');
    const [missingD103Form, setMissingD103Form] = useState<boolean>(true);
    const [skipWelcomeLetter, setSkipWelcomeLetter] = useState<boolean>(false);

    
    useEffect(() => {
        setForm(props.formData);
    }, [props.formData]);

    useEffect(() => {
        if (props.formData.officeSought?.districtCode)
            resetOfficeType(props.formData.officeSought.districtCode); 
    }, [props.formData]);

    useEffect(() => {
        setIsReopen(props.formData.isReopen);
    }, [props.formData]);

    useEffect(() => {
        if (props.formData.ocpfCommitteeName) {
            setCommitteeName(props.formData.ocpfCommitteeName);
        }
    }, [props.formData.ocpfCommitteeName]);


    useEffect(() => {
        fetch(`/Reference/GetActiveDistrictsByOfficeGroup?officeTypeGroupId=${officeType}`)
            .then(res => res.json())
            .then(data => {
                let districtList = data;
                setDistricts(districtList);
                setDistrictId(-1);
            });
    }, [officeType]);

    //after getting the district list, populate the district id from the form
    useEffect(() => {
        if (props.formData.officeSought?.districtCode && districts.length > 0) {
            const match = districts.find((d: District) => d.code === Number(form.officeSought?.districtCode));
            if (match) {
                setDistrictId(match.code);
            }
        }
    }, [districts, props.formData.officeSoughtDistrictCode]);

    const resetOfficeType = (districtCode: number) => {

        if (!districtCode) return;

        let code = Number(districtCode);

        if (code <= 199) {
            // senate
            setOfficeType('2');
            setAccountTypeCode('U');
        } else if (code <= 399) {
            // house
            setOfficeType('1');
            setAccountTypeCode('U');
        } else if (code >= 500 && code <= 599) {
            // Sheriff
            setOfficeType('6');
        } else if (code >= 600 && code <= 699) {
            // District Attorney
            setOfficeType('5');
        } else if (code >= 1101 && code <= 1108) {
            // Governor's Council
            setOfficeType('4');
        } else if (code >= 1109 && code <= 1114) {
            // Statewide
            setOfficeType('3');
        } else if (code >= 4000 && code <= 4999) {
            // Mayoral
            setOfficeType('8');
        } else if (code >= 5000 && code <= 5999) {
            // City Councilor
            setOfficeType('9');
        } else {
            setOfficeType('10');
        }
    }

    const isCandidate = (): boolean => {
        return accountTypeCode === 'U' || accountTypeCode === 'D';
    }

    const isDepository = (): boolean => {
        return accountTypeCode === 'U' || accountTypeCode === 'D' || accountTypeCode === 'P';
    }

    const isLabelNameNeeded = (): boolean => {
        return accountTypeCode === 'P' || accountTypeCode === 'X' || accountTypeCode === 'I';
    }

    const updateAuditorId = (auditorId: number) => {
        setAuditorId(auditorId);
    }

    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
        if (alertInfo?.type === 'success') {
            props.closeGenericForm();
        }
    }

    const saveOrganizationFormInfo = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        formData.append("accountTypeCode", accountTypeCode.toString());
        formData.append("auditorId", auditorId.toString());
        formData.append("cpfId", form.cpfID);
        formData.append("isCandidateOnly", isCandidateOnly.toString());
        formData.append("missingD103", missingD103Form.toString());
        formData.append("skipWelcomeLetter", skipWelcomeLetter.toString());
        formData.append("url", form.formIntakeBlobUrl);

        let saveFilerUrl = isReopen ? '/LLMFormProcessor/ReactivateFiler' : '/LLMFormProcessor/AddFiler';

        fetch(saveFilerUrl,
            {
                method: 'POST',
                body: formData,
            })
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: Add/Reactivate Filer',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let info = {
                        type: 'success',
                        title: 'The New Filer Info Saved',
                        errorMessage: 'The 101 form was posted.\nThe new filer info was saved.\nThe draft archer letters were created in Archer.',
                        secondaryErrorMessage: ''
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                    //open profiles??
                }
            });
    }

    return (
        <>
            <form onSubmit={(event: React.FormEvent<HTMLFormElement>) => saveOrganizationFormInfo(event)}>
                <Paper sx={{ p: 3 }} >
                    <Grid item xs={12} marginTop={1} >
                        <Stack spacing={2}>
                            <FormControlLabel
                                control={<Checkbox checked={isReopen} color="primary" onChange={(e) => setIsReopen(e.target.checked)} />}
                                label={`Reopen Closed Account?`}
                            />
                            <FormControl sx={{ m: 1, width: 400 }} size="small">
                                <InputLabel>Account Type</InputLabel>
                                <Select
                                    label="Account Type"
                                    value={accountTypeCode}
                                    onChange={(event) => setAccountTypeCode(event.target.value)}
                                >
                                    <MenuItem value={'D'}>D Depository Candidate</MenuItem>
                                    <MenuItem value={'U'}>U Legislative Candidate</MenuItem>
                                    <MenuItem value={'P'}>P Traditional PAC</MenuItem>
                                    <MenuItem value={'X'}>X Ballot Question Committee</MenuItem>
                                    <MenuItem value={'I'}>I IEPAC</MenuItem>
                                    <MenuItem value={'V'}>V Segregated Fund</MenuItem>
                                    <MenuItem value={'L'}>L Local Party Committee</MenuItem>
                                </Select>
                            </FormControl>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    sx={{ minWidth: 100, width: 350 }}
                                    name="organizationDate"
                                    label="Organization Date"
                                    defaultValue={dayjs()}
                                    slotProps={{ textField: { required: true } }}
                                />
                            </LocalizationProvider>
                            {
                                isCandidate() &&
                                <Stack direction="row" spacing={1}>
                                    <FormControl sx={{ m: 1, width: 200 }} size="small">
                                        <InputLabel id="office-select-label">Select an office</InputLabel>
                                        <Select
                                            labelId="office-select-label"
                                            id="newOfficeCodeSought"
                                            label="Select an office"
                                            value={officeType}
                                            onChange={(event) => setOfficeType(event.target.value)}
                                        >
                                            <MenuItem value="">
                                                <em>Select an office:</em>
                                            </MenuItem>
                                            <MenuItem value="1">House</MenuItem>
                                            <MenuItem value="2">Senate</MenuItem>
                                            <MenuItem value="3">Statewide</MenuItem>
                                            <MenuItem value="4">Governor's Council</MenuItem>
                                            <MenuItem value="5">District Attorney</MenuItem>
                                            <MenuItem value="6">Sheriff</MenuItem>
                                            <MenuItem value="8">Mayoral (Depository)</MenuItem>
                                            <MenuItem value="9">City Councilor</MenuItem>
                                            <MenuItem value="10">Other Depository</MenuItem>
                                            <MenuItem value="11">Local (R7)</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                                        <InputLabel id="district-select-label">Select a District</InputLabel>
                                        <Select
                                            label="Select a District"
                                            value={districtId}
                                            name="districtCodeSought"
                                            onChange={(event) => setDistrictId(Number(event.target.value))}
                                        >
                                            <MenuItem value={-1}>Select a District</MenuItem>
                                            {
                                                districts && districts.map((district) => (
                                                    <MenuItem key={district.code} value={district.code}>{district.description}</MenuItem>
                                                ))
                                            }
                                        </Select>
                                    </FormControl>
                                </Stack>
                            }
                            {
                                isCandidate() && 
                                <Stack spacing={0}>
                                    <FormControlLabel
                                        control={<Checkbox checked={isCandidateOnly} color="primary" onChange={(e) => setIsCandidateOnly(e.target.checked)} />}
                                        label={`Is Candidate-Only?`}
                                    />
                                </Stack>
                            }
                            {
                                !isCandidateOnly &&
                                <TextField
                                    margin="normal"
                                    name="committeeName"
                                    label="OCPF Committee Name"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    value={committeeName}
                                    onChange={(e)=>setCommitteeName(e.target.value)}
                                />
                            }
                            {
                                isLabelNameNeeded() &&
                                <TextField
                                    margin="normal"
                                    name="labelName"
                                    label="Label Name"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                />
                            }
                            <AuditorsDropdown auditorId={auditorId} auditors={AUDITORS} updateAuditorId={updateAuditorId}></AuditorsDropdown>

                            { /*Party Info*/}
                            {
                                (isCandidate() || accountTypeCode === 'L') && 
                                <FormControl sx={{ m: 1, width: 200 }} size="small">
                                    <InputLabel>Party Affiliation</InputLabel>
                                    <Select
                                        label="Party Affiliation"
                                        name= "PartyAffiliation"
                                        defaultValue={partyAffiliation || ''}
                                    >
                                        <MenuItem value={'Democratic'}>Democratic</MenuItem>
                                        <MenuItem value={'Republican'}>Republican</MenuItem>
                                        <MenuItem value={'Unenrolled'}>Unenrolled</MenuItem>
                                        <MenuItem value={'Green-Rainbow Party'}>Green-Rainbow Party</MenuItem>
                                        <MenuItem value={'Libertarian'}>Libertarian</MenuItem>
                                    </Select>
                                </FormControl>
                            }
                            
                            { /*Candidate Info*/}
                            {
                                isCandidate() &&
                                <>
                                    <Typography variant="subtitle2" fontWeight={550} color="primary" >CANDIDATE CONTACT INFORMATION</Typography>
                                    <Stack direction="row" spacing={1} flexWrap={isSmall ? 'wrap' : 'nowrap'} >
                                        <FormControl sx={{ m: 1, minWidth: 120 }} size="small" error={candidateSalutation === ''} required>
                                            <InputLabel>Salutation</InputLabel>
                                            <Select
                                                name="candidateSalutation"
                                                label="Salutation"
                                                value={candidateSalutation}
                                                onChange={(e) => setCandidateSalutation(e.target.value)}
                                                required
                                            >
                                                <MenuItem value={'Mr.'}>Mr.</MenuItem>
                                                <MenuItem value={'Ms.'}>Ms.</MenuItem>
                                                <MenuItem value={'Mx.'}>Mx.</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            margin="normal"
                                            name="candidateFirstName"
                                            label="First Name"
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            defaultValue={form.candidate?.firstName}
                                            required
                                        />
                                        <TextField
                                            margin="normal"
                                            name="candidateMiddleName"
                                            label="Middle Name"
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            defaultValue={form.candidate?.middleName}
                                        />
                                        <TextField
                                            margin="normal"
                                            name="candidateLastName"
                                            label="Last Name"
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            defaultValue={form.candidate?.lastName}
                                            required
                                        />
                                        <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                                            <InputLabel>Suffix</InputLabel>
                                            <Select
                                                name="candidateSuffix"
                                                label="Suffix"
                                                defaultValue=''
                                            >
                                                <MenuItem value={'II'}>II</MenuItem>
                                                <MenuItem value={'III'}>III</MenuItem>
                                                <MenuItem value={'IV'}>IV</MenuItem>
                                                <MenuItem value={'Jr.'}>Jr.</MenuItem>
                                                <MenuItem value={'Sr.'}>Sr.</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Stack>
                                    <TextField
                                        margin="normal"
                                        name="candidateStreetAddress"
                                        label="Street Address"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        defaultValue={form.candidate?.residentialAddress}
                                    />
                                    <Stack direction="row" spacing={1} flexWrap={isSmall ? 'wrap' : 'nowrap'}>
                                        <TextField
                                            margin="normal"
                                            name="candidateCareOf"
                                            label="Care Of"
                                            InputLabelProps={{ shrink: true }}
                                            size="small"
                                        />
                                        <TextField
                                            margin="normal"
                                            name="candidateCity"
                                            label="City"
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            defaultValue={form.candidate?.city}
                                        />
                                        <TextField
                                            margin="normal"
                                            name="candidateState"
                                            label="State"
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            defaultValue={form.candidate?.state}
                                        />
                                        <TextField
                                            margin="normal"
                                            name="candidateZipCode"
                                            label="Zip Code"
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            defaultValue={form.candidate?.zipCode}
                                        />
                                    </Stack>
                                    <TextField
                                        margin="normal"
                                        name="candidateEmailAddress"
                                        label="Email Address"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        defaultValue={form.candidate?.emailAddress}
                                    />
                                    <TextField
                                        margin="normal"
                                        name="candidatePhoneNumber"
                                        label="Phone Number"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        defaultValue={form.candidate?.phoneNumber}
                                    />
                                </>
                            }
                            { /*Treasurer Info*/}
                            {!isCandidateOnly &&
                                <>
                                <Typography variant="subtitle2" fontWeight={550} color="primary" >TREASURER CONTACT INFORMATION</Typography>
                                <Stack direction="row" spacing={1} flexWrap={isSmall ? 'wrap' : 'nowrap'}>
                                    <FormControl sx={{ m: 1, minWidth: 120 }} size="small" error={treasurerSalutation === ''} required>
                                        <InputLabel>Salutation</InputLabel>
                                        <Select
                                            name="treasurerSalutation"
                                            label="Salutation"
                                            value={treasurerSalutation}
                                            onChange={(e) => setTreasurerSalutation(e.target.value)}
                                            required
                                        >
                                            <MenuItem value={'Mr.'}>Mr.</MenuItem>
                                            <MenuItem value={'Ms.'}>Ms.</MenuItem>
                                            <MenuItem value={'Mx.'}>Mx.</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        margin="normal"
                                        name="treasurerFirstName"
                                        label="First Name"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        defaultValue={form.treasurer?.firstName}
                                        required
                                    />
                                    <TextField
                                        margin="normal"
                                        name="treasurerMiddleName"
                                        label="Middle Name"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        defaultValue={form.treasurer?.middleName}
                                    />
                                    <TextField
                                        margin="normal"
                                        name="treasurerLastName"
                                        label="Last Name"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        defaultValue={form.treasurer?.lastName}
                                        required
                                    />
                                    <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                                        <InputLabel>Suffix</InputLabel>
                                        <Select
                                            name="treasurerSuffix"
                                            label="Suffix"
                                            defaultValue=""
                                        >
                                            <MenuItem value={'II'}>II</MenuItem>
                                            <MenuItem value={'III'}>III</MenuItem>
                                            <MenuItem value={'IV'}>IV</MenuItem>
                                            <MenuItem value={'Jr.'}>Jr.</MenuItem>
                                            <MenuItem value={'Sr.'}>Sr.</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Stack>
                                <TextField
                                    margin="normal"
                                    name="treasurerStreetAddress"
                                    label="Street Address"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    defaultValue={form.treasurer?.residentialAddress}
                                />
                                <Stack direction="row" spacing={1} flexWrap={isSmall ? 'wrap' : 'nowrap'}>
                                    <TextField
                                        margin="normal"
                                        name="treasurerCareOf"
                                        label="Care Of"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                    <TextField
                                        margin="normal"
                                        name="treasurerCity"
                                        label="City"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        defaultValue={form.treasurer?.city}
                                    />
                                    <TextField
                                        margin="normal"
                                        name="treasurerState"
                                        label="State"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        defaultValue={form.treasurer?.state}
                                    />
                                    <TextField
                                        margin="normal"
                                        name="treasurerZipCode"
                                        label="Zip Code"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        defaultValue={form.treasurer?.zipCode}
                                    />
                                </Stack>
                                <TextField
                                    margin="normal"
                                    name="treasurerEmailAddress"
                                    label="Email Address"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    defaultValue={form.treasurer?.emailAddress || form.treasurer?.email}
                                />
                                <TextField
                                    margin="normal"
                                    name="treasurerPhoneNumber"
                                    label="Phone Number"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    defaultValue={form.treasurer?.phoneNumber}
                                />
                            </>
                            }
                            { /*Chair Info*/}
                            {!isCandidateOnly &&
                                <>
                                    <Typography variant="subtitle2" fontWeight={550} color="primary" >CHAIR CONTACT INFORMATION</Typography>
                                <Stack direction="row" spacing={1} flexWrap={isSmall ? 'wrap' : 'nowrap'}>
                                    <FormControl sx={{ m: 1, minWidth: 120 }} size="small" error={chairSalutation === ''} required>
                                            <InputLabel>Salutation</InputLabel>
                                            <Select
                                                name="chairmanSalutation"
                                                label="Salutation"
                                                value={chairSalutation}
                                                onChange={(e) => setChairSalutation(e.target.value)}
                                                required
                                            >
                                                <MenuItem value={'Mr.'}>Mr.</MenuItem>
                                                <MenuItem value={'Ms.'}>Ms.</MenuItem>
                                                <MenuItem value={'Mx.'}>Mx.</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            margin="normal"
                                            name="chairmanFirstName"
                                            label="First Name"
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            defaultValue={getChairInfo(form)?.firstName}
                                            required
                                        />
                                        <TextField
                                            margin="normal"
                                            name="chairmanMiddleName"
                                            label="Middle Name"
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            defaultValue={getChairInfo(form)?.middleName}
                                        />
                                        <TextField
                                            margin="normal"
                                            name="chairmanLastName"
                                            label="Last Name"
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            defaultValue={getChairInfo(form)?.lastName}
                                            required
                                        />
                                        <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                                            <InputLabel>Suffix</InputLabel>
                                            <Select
                                                name="chairmanSuffix"
                                                label="Suffix"
                                                defaultValue=''
                                            >
                                                <MenuItem value={'II'}>II</MenuItem>
                                                <MenuItem value={'III'}>III</MenuItem>
                                                <MenuItem value={'IV'}>IV</MenuItem>
                                                <MenuItem value={'Jr.'}>Jr.</MenuItem>
                                                <MenuItem value={'Sr.'}>Sr.</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Stack>
                                    <TextField
                                        margin="normal"
                                        name="chairmanStreetAddress"
                                        label="Street Address"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        defaultValue={getChairInfo(form)?.residentialAddress}
                                    />
                                    <Stack direction="row" spacing={1} flexWrap={isSmall ? 'wrap' : 'nowrap'}>
                                        <TextField
                                            margin="normal"
                                            name="chairmanCareOf"
                                            label="Care Of"
                                            InputLabelProps={{ shrink: true }}
                                            size="small"
                                        />
                                        <TextField
                                            margin="normal"
                                            name="chairmanCity"
                                            label="City"
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            defaultValue={getChairInfo(form)?.city}
                                        />
                                        <TextField
                                            margin="normal"
                                            name="chairmanState"
                                            label="State"
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            defaultValue={getChairInfo(form)?.state}
                                        />
                                        <TextField
                                            margin="normal"
                                            name="chairmanZipCode"
                                            label="Zip Code"
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            defaultValue={getChairInfo(form)?.zipCode}
                                        />
                                    </Stack>
                                    <TextField
                                        margin="normal"
                                        name="chairmanEmailAddress"
                                        label="Email Address"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        defaultValue={getChairInfo(form)?.emailAddress || getChairInfo(form)?.email}
                                    />
                                    <TextField
                                        margin="normal"
                                        name="chairmanPhoneNumber"
                                        label="Phone Number"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        defaultValue={getChairInfo(form)?.phoneNumber}
                                    />
                                </>
                            }
                            {
                                isDepository() && 
                                <FormControlLabel
                                    control={<Checkbox checked={missingD103Form} color="primary" onChange={(e) => setMissingD103Form(e.target.checked)} />}
                                    label="Missing a D103?"
                                />
                            }
                            <FormControlLabel
                                control={<Checkbox checked={skipWelcomeLetter} color="primary" onChange={(e) => setSkipWelcomeLetter(e.target.checked)} />}
                                label={`Skip Welcome Letter?`}
                            />
                        </Stack>
                    </Grid>
                </Paper>
                <Grid item xs={12} marginTop={3} marginBottom={3} >
                    <Stack direction="row" spacing={2} flexWrap={isSmall ? 'wrap' : 'nowrap'} justifyContent={"flex-end"} marginTop={2}>
                        <Button variant="contained" color="warning" onClick={props.closeGenericForm}>Cancel</Button>
                        <Button variant="contained" type="submit">Save</Button>
                    </Stack>
                </Grid>
            </form>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </>
    )
}

export default OrganizationForm;